import { AfterViewInit, Component, ElementRef, ErrorHandler, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DwComponent, DwComponentType, DwRoutingService } from '@devwareapps/devware-cap';
import { CourseEntity, CourseLessonEntity, CourseModuleEntity } from '../../../../meta-data/app-meta-data.service';
import { CourseRepositoryService } from '../../services/course-repository.service';
import { CourseNavigationService } from '../../services/course-navigation.service';
import {
  CourseProgressItem, ModuleProgressItem
} from '../../models/course-progress-hierarchy.model';

type ModuleState = 'completed' | 'in-progress' | 'not-started';

interface ModuleRow {
  ref: ModuleProgressItem;
  state: ModuleState;
  statusLabel: string;       // "Completed" | "In progress" | "Not started"
  lessonsCompleted: number;
  lessonsTotal: number;
  percentComplete: number;
  runtimeLabel: string;      // "2h 53m"
  descriptionExpanded: boolean;
}

interface FilterChip {
  // 'endorsement' is a view-toggle, not a module filter: selecting it swaps the
  // module accordion for the inline Endorsement & ACS panel.
  key: 'all' | 'in-progress' | 'completed' | 'not-started' | 'endorsement';
  label: string;
  count: number;
  active: boolean;
}

@DwComponent({
  key: 'CousrseDetailPage',
  name: 'Course DetailPage',
  componentType: DwComponentType.pageComponent,
  isGlobal: true
})
@Component({
  selector: 'app-course-detail-view',
  templateUrl: './course-detail-view.component.html',
  styleUrls: ['./course-detail-view.component.scss']
})
export class CourseDetailViewComponent implements OnInit, AfterViewInit, OnDestroy {
  course: CourseEntity | null = null;
  courseProgress: CourseProgressItem | null = null;
  // Set when the read errors or returns no course (not enrolled / deleted), so
  // the template swaps the skeleton for the unavailable + Retry state.
  loadFailed = false;

  modulesCompleted = 0;
  modulesTotal = 0;
  lessonsCompleted = 0;
  lessonsTotal = 0;
  progressPercent = 0;
  totalRuntimeLabel = '';

  // Count badge on the Endorsements pill. Derived from the student's endorsement
  // state on the course (1 when endorsed, else 0) — no extra fetch; the progress
  // query already carries studentCourse.IsEndorsed.
  endorsementCount = 0;

  ringDashArray = '0 100';   // SVG stroke-dasharray for the % ring

  modules: ModuleRow[] = [];
  allModules: ModuleRow[] = [];
  chips: FilterChip[] = [];

  // Mobile horizontal-scroll affordance for the chip row. On phones the pills
  // overflow into a single swipeable line; these flags drive an edge-fade mask
  // (see .cd-chips--fade-start / --fade-end) that dissolves a pill only toward
  // an edge with more content past it. Both false when the row fits (e.g. the
  // endorsed 2-chip row), so nothing fades when there's nothing to scroll to.
  @ViewChild('chipScroll') private chipScrollRef?: ElementRef<HTMLElement>;
  chipsFadeStart = false;
  chipsFadeEnd = false;

  hasResumeTarget = false;

  // First-lesson navigation target used as a fallback for the hero CTA when
  // the student has no resume target (i.e. brand-new course, no progress
  // anywhere). Lets the button do useful work — "Start course" → first
  // lesson — instead of rendering disabled. Null only when the course has
  // zero modules or zero lessons in the first module.
  private firstLessonTarget: { moduleId: number; lessonId: number } | null = null;

  // True when the Endorsements pill is active — the template hides the module
  // accordion and renders the inline Endorsement & ACS panel instead.
  get isEndorsementView(): boolean { return this.chips.find(c => c.active)?.key === 'endorsement'; }

  get hasStartTarget(): boolean { return this.firstLessonTarget != null; }
  get isComplete(): boolean { return this.progressPercent >= 100; }
  // At 100% there's nothing to "resume" — the CTA reopens the course to review.
  // Otherwise: Resume (has progress) or Start (brand-new).
  get heroCtaLabel(): string {
    if (this.isComplete) { return 'Review course'; }
    return this.hasResumeTarget ? 'Resume training' : 'Start course';
  }
  get heroCtaDisabled(): boolean { return !this.hasResumeTarget && !this.hasStartTarget; }

  // Mobile-only "Show more" toggle for the hero description. The hero blurb
  // is clamped to a few lines on narrow viewports (CSS-driven) and this flag
  // unclamps it inline when the user taps the toggle. Desktop ignores both.
  heroBlurbExpanded = false;

  private destroy$ = new Subject<void>();
  private pendingCourseId = 0;
  private loadSubscription?: Subscription;

  constructor(
    private router: Router,
    private dwRoutingService: DwRoutingService,
    private courseRepositoryService: CourseRepositoryService,
    private courseNavigationService: CourseNavigationService,
    private errorHandler: ErrorHandler,
  ) {}

  ngOnInit(): void {
  //  this.loadFromUrl(this.router.url);
    this.setupCourse();

    this.dwRoutingService.getRoutesChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.setupCourse());
  }

  ngAfterViewInit(): void {
    // Chips may already be present (synchronous restore) — measure once the
    // view exists. Async data loads also re-measure via buildChips().
    this.updateChipFades();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onWindowResize(): void { this.updateChipFades(); }

  trackModule = (_: number, m: ModuleRow) => m.ref.module.CourseModuleId;

  // Compares the chip row's scroll offset against its overflow to decide which
  // edges still have hidden pills, toggling the fade mask accordingly. Tolerant
  // of sub-pixel/scroll-snap rounding (the ±1 guards).
  updateChipFades(): void {
    const el = this.chipScrollRef?.nativeElement;
    if (!el) { this.chipsFadeStart = this.chipsFadeEnd = false; return; }
    const maxScroll = el.scrollWidth - el.clientWidth;
    this.chipsFadeStart = el.scrollLeft > 1;
    this.chipsFadeEnd = maxScroll > 1 && el.scrollLeft < maxScroll - 1;
  }

  selectChip(chipKey: FilterChip['key'], event?: Event): void {
    this.chips = this.chips.map(c => ({ ...c, active: c.key === chipKey }));
    this.persistChip(chipKey);
    this.filterModules();

    // On mobile the tapped pill may be partly off-screen in the swipeable row
    // (e.g. Endorsements at the far right). Bring it fully into view instead of
    // leaving it clipped under the edge fade. inline:'nearest' scrolls only the
    // chip row; block:'nearest' avoids nudging the page vertically.
    // behavior omitted → follows the row's CSS scroll-behavior (smooth, but
    // auto under prefers-reduced-motion).
    const pill = event?.currentTarget as HTMLElement | null;
    pill?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }

  toggleHeroBlurb(): void {
    this.heroBlurbExpanded = !this.heroBlurbExpanded;
  }

  toggleModuleDescription(row: ModuleRow, event: Event): void {
    // Card header is itself a click target that navigates to the module page —
    // stopPropagation so the Show more toggle doesn't also fire the navigate.
    event.stopPropagation();
    row.descriptionExpanded = !row.descriptionExpanded;
  }

  // Resume → furtherest lesson when one exists; otherwise Start → first lesson
  // of the first module. Same handler so the CTA stays in one place; the label
  // and target are derived from progress state.
  resumeCourse(): void {
    if (!this.course) { return; }
    const lesson = this.courseProgress?.furtherestLesson;
    if (lesson) {
      this.courseNavigationService.navigateToLesson({
        courseId: this.course.CourseId,
        moduleId: lesson.CourseModuleId,
        lessonId: lesson.CourseLessonId,
      });
      return;
    }
    if (this.firstLessonTarget) {
      this.courseNavigationService.navigateToLesson({
        courseId: this.course.CourseId,
        moduleId: this.firstLessonTarget.moduleId,
        lessonId: this.firstLessonTarget.lessonId,
      });
    }
  }

  navigateToModule(module: CourseModuleEntity): void {
    if (!this.course) { return; }
    this.courseNavigationService.navigateToModule(this.course.CourseId, module.CourseModuleId);
  }

  setupCourse() {
    const lessonReferenceInfo = this.courseNavigationService.getCurrentCourseParameters();

    const courseId = lessonReferenceInfo?.courseId ?? null;

    if (!courseId) {
      this.course = null;
      return;
    }

    // // Course-only route — strict match prevents this loader from firing on
    // // /my-courses/view/:cid/module/... where a different view owns the column.
    // const match = url.match(/^\/my-courses\/view\/(\d+)\/?(?:\?.*)?$/);
    // if (!match) {
    //   this.course = null;
    //   return;
    // }

    // const courseId = parseInt(match[1], 10);

    if (courseId !== this.pendingCourseId) {
      this.course = null;
      this.courseProgress = null;
      this.modules = [];
      this.chips = [];
    }
    this.pendingCourseId = courseId;
    this.loadFailed = false;

    // Drop the previous read so a late response/error from an older course
    // can't land on this one.
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.courseRepositoryService.getCourseProgress(courseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: progress => {
          this.courseProgress = progress;
          this.course = progress?.course ?? null;
          this.loadFailed = !this.course;
          if (this.loadFailed) { return; }
          this.applyHeader();
          this.buildModules();
          this.buildChips();
        },
        error: err => {
          this.course = null;
          this.loadFailed = true;
          // Devware's global handler logs (console/server) without a toast;
          // the inline Retry state is the user-facing message.
          this.errorHandler.handleError(err);
        }
      });
  }

  private applyHeader(): void {
    if (!this.courseProgress) { return; }

    const modules = this.courseProgress.modules ?? [];
    this.modulesTotal = modules.length;
    this.modulesCompleted = modules.filter(m => this.deriveModuleState(m) === 'completed').length;
    this.lessonsCompleted = this.courseProgress.completedLessons ?? 0;
    this.lessonsTotal = this.courseProgress.totalLessons ?? 0;
    this.progressPercent = Math.max(0, Math.min(100, Math.round(this.courseProgress.percentComplete ?? 0)));

    // SVG ring uses pathLength="100" so dasharray is "<percent> 100".
    this.ringDashArray = `${this.progressPercent} 100`;

    this.totalRuntimeLabel = this.formatHoursMinutes(this.course?.TotalCourseTimeSeconds);

    this.hasResumeTarget = !!this.courseProgress.furtherestLesson;
    this.firstLessonTarget = this.resolveFirstLessonTarget(modules);

    this.endorsementCount = this.courseProgress.studentCourse?.IsEndorsed ? 1 : 0;
  }

  // Walks the modules in ModuleOrder, finds the first module with at least one
  // lesson, and returns the smallest-LessonOrder lesson there. Used by the
  // "Start course" fallback so a brand-new student lands on lesson 1.1 instead
  // of staring at a disabled CTA.
  private resolveFirstLessonTarget(items: ModuleProgressItem[]): { moduleId: number; lessonId: number } | null {
    const sortedModules = items.slice()
      .sort((a, b) => (a.module?.ModuleOrder ?? 0) - (b.module?.ModuleOrder ?? 0));
    for (const m of sortedModules) {
      const lessons = m.lessons ?? [];
      if (lessons.length === 0) { continue; }
      const firstLesson: CourseLessonEntity | undefined = lessons.slice()
        .sort((a, b) => (a.lesson?.LessonOrder ?? 0) - (b.lesson?.LessonOrder ?? 0))[0]?.lesson;
      if (firstLesson) {
        return { moduleId: m.module.CourseModuleId, lessonId: firstLesson.CourseLessonId };
      }
    }
    return null;
  }

  private buildModules(): void {
    const items = (this.courseProgress?.modules ?? []).slice()
      .sort((a, b) => (a.module?.ModuleOrder ?? 0) - (b.module?.ModuleOrder ?? 0));

    this.allModules = items.map(item => {
      const state = this.deriveModuleState(item);
      const lessonsCompleted = item.completedLessons ?? 0;
      const lessonsTotal = item.totalLessons ?? item.lessons?.length ?? 0;
      const percentComplete = lessonsTotal > 0
        ? Math.round((lessonsCompleted / lessonsTotal) * 100)
        : 0;
      const runtimeLabel = this.formatHoursMinutes(this.sumModuleSeconds(item));

      return {
        ref: item,
        state,
        statusLabel: this.moduleStatusLabel(state),
        lessonsCompleted,
        lessonsTotal,
        percentComplete,
        runtimeLabel,
        descriptionExpanded: false,
      };
    });

    this.filterModules();
  }

  filterModules() {
    const activeChip = this.chips.find(c => c.active);
    if (!activeChip || activeChip.key === 'all') {
      this.modules = [...this.allModules];
      return;
    }
    this.modules = this.allModules.filter(m => m.state === activeChip.key);
  }

  private buildChips(): void {
    // Counts come from allModules so activating a filter doesn't shrink the
    // *other* chips' counts (which would mislead the user about how many
    // items each filter would show).
    const inProgress = this.allModules.filter(m => m.state === 'in-progress').length;
    const completed = this.allModules.filter(m => m.state === 'completed').length;
    const notStarted = this.allModules.filter(m => m.state === 'not-started').length;

    // Restore the user's previous selection — both within this component
    // instance (background data refresh) and across instances (navigate to a
    // lesson and back). Falls through to "all" the first time.
    const previousActive = this.chips.find(c => c.active)?.key ?? this.restoreChipKey();

    const allChips: FilterChip[] = [
      { key: 'all',          label: 'All',          count: this.allModules.length, active: false },
      { key: 'in-progress',  label: 'In progress',  count: inProgress,             active: false },
      { key: 'completed',    label: 'Completed',    count: completed,              active: false },
      { key: 'not-started',  label: 'Not started',  count: notStarted,             active: false },
      // View-toggle, always present. Count is 1 when the student is endorsed.
      { key: 'endorsement',  label: 'Endorsements', count: this.endorsementCount,  active: false },
    ];

    // Once endorsed (course complete) the module-state filters are redundant —
    // every module is Completed, so All / In progress / Not started carry no
    // signal. Collapse the row to Completed + Endorsements. Not endorsed shows
    // the full set.
    const isEndorsed = this.endorsementCount > 0;
    this.chips = isEndorsed
      ? allChips.filter(c => c.key === 'completed' || c.key === 'endorsement')
      : allChips;

    // Restored/previous key may point at a chip that's now hidden (e.g. 'all'
    // when endorsed) — fall back to the first visible chip (Completed).
    const target = this.chips.find(c => c.key === previousActive) ?? this.chips[0];
    target.active = true;

    // buildModules ran filterModules with stale chips state; re-apply now
    // that the restored selection is in place.
    this.filterModules();

    // The chip count can switch the row between 5 and 2 pills; recompute the
    // mobile edge-fade once the *ngFor has rendered the new set.
    setTimeout(() => this.updateChipFades());
  }

  // Per-course sessionStorage so the active filter chip survives a navigate
  // away (e.g. into a lesson) and back. Wrapped in try/catch because Safari's
  // private mode + some embedded iframes throw on storage access.
  private chipStorageKey(): string | null {
    return this.pendingCourseId ? `aviator.cd-filter.${this.pendingCourseId}` : null;
  }

  private persistChip(key: FilterChip['key']): void {
    const storageKey = this.chipStorageKey();
    if (!storageKey) { return; }
    try { window.sessionStorage.setItem(storageKey, key); } catch { /* ignore */ }
  }

  private restoreChipKey(): FilterChip['key'] {
    const storageKey = this.chipStorageKey();
    if (!storageKey) { return 'all'; }
    try {
      const value = window.sessionStorage.getItem(storageKey);
      if (value === 'in-progress' || value === 'completed' || value === 'not-started'
        || value === 'all' || value === 'endorsement') {
        return value;
      }
    } catch { /* ignore */ }
    return 'all';
  }

  private deriveModuleState(item: ModuleProgressItem): ModuleState {
    const total = item.totalLessons ?? item.lessons?.length ?? 0;
    const done = item.completedLessons ?? 0;
    if (total > 0 && done >= total) { return 'completed'; }
    if (done > 0) { return 'in-progress'; }
    return 'not-started';
  }

  private moduleStatusLabel(state: ModuleState): string {
    switch (state) {
      case 'completed':   return 'Completed';
      case 'in-progress': return 'In progress';
      default:            return 'Not started';
    }
  }

  private sumModuleSeconds(item: ModuleProgressItem): number {
    const fromModule = (item.module as any)?.TotalModuleTimeSeconds;
    if (typeof fromModule === 'number' && fromModule > 0) { return fromModule; }
    return (item.lessons ?? []).reduce(
      (sum, l) => sum + (l.durationSeconds ?? 0),
      0
    );
  }

  private formatHoursMinutes(totalSeconds?: number): string {
    const s = Math.max(0, Math.round(totalSeconds ?? 0));
    if (s === 0) { return '0m'; }
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    if (hours > 0 && minutes > 0) { return `${hours}h ${minutes}m`; }
    if (hours > 0) { return `${hours}h`; }
    return `${minutes}m`;
  }
}
